import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import getSendData from '../../../reports/getSendData';
import formatHistoryData from '../../../utils/formatHistoryData';
import createPDF from '../../../utils/createPDF';
import fetchSRS from '../../../utils/fetchSRS';
import hasRequiredSelection, {
    MISSING_SELECTION_MESSAGE_KEY,
} from '../../../reports/hasRequiredSelection';

export const useHandleSubmit = ({
    user,
    filters,
    client,
    closeDrawer,
    sendNotification,
    addReportDownloading,
    updateReportDownloading,
    addReport,
    setReportDownloadState,
    rightsholderAttribution,
}) => {
    const { t } = useTranslation('tools');

    return useCallback(
        async (values, { setSubmitting }) => {
            if (!hasRequiredSelection(values)) {
                sendNotification(t(MISSING_SELECTION_MESSAGE_KEY), {
                    context: 'danger',
                });
                setSubmitting(false);
                return;
            }

            const cleanValues = JSON.parse(JSON.stringify(values));

            if (Array.isArray(cleanValues.rightsholders)) {
                cleanValues.rhWithSP = [...cleanValues.rightsholders];
                cleanValues.rightsholders = cleanValues.rightsholders.reduce(
                    (acc, rhId) => {
                        acc[rhId] = true;
                        return acc;
                    },
                    {},
                );
            }

            const reportDownloadingId = `${cleanValues.reportTitle}_${Date.now()}`;
            const prev = cleanValues;
            const rightsholderIds = prev.rhWithSP.filter(
                rhId => prev.rightsholders[rhId],
            );
            const multipleRightsholders = rightsholderIds.length > 1;

            closeDrawer?.();

            try {
                addReportDownloading({
                    title: prev.reportTitle,
                    id: reportDownloadingId,
                });

                const sendData = getSendData({
                    user,
                    filters,
                    client,
                    values: cleanValues,
                    rightsholderAttribution,
                });

                const getHistoryData = formatHistoryData({
                    filters,
                    user,
                    values: prev,
                    multipleRightsholders,
                    rightsholderIds,
                });

                if (prev.exportAs.toLowerCase() === 'pdf') {
                    const { pdfUrl: reportUrl, reportId: id } = await createPDF(
                        {
                            reqBody: sendData,
                            type: 'pdf',
                            document,
                            filename: prev.reportTitle,
                            notif: sendNotification,
                        },
                    );

                    const data = getHistoryData({ reportUrl, id });
                    await Promise.all([
                        addReport([data]),
                        updateReportDownloading({
                            id: reportDownloadingId,
                            isDownloaded: true,
                            isDownloading: false,
                        }),
                    ]);

                    sendNotification('PDF report downloaded!', {
                        context: 'success',
                    });
                } else {
                    const { createDateReportIdSortKey } = (await fetchSRS
                        .url(`/reports`)
                        .json({ action: 'generateppt', data: sendData })
                        .post()
                        .json()) as { createDateReportIdSortKey: string };

                    setReportDownloadState({
                        loadingReportId: createDateReportIdSortKey,
                        reportDownloadingId,
                    });
                }
            } catch (err) {
                console.error(err);
                sendNotification(
                    'There was a problem generating your report. Please try again.',
                    { context: 'danger' },
                );

                await updateReportDownloading({
                    id: reportDownloadingId,
                    isDownloaded: false,
                    isDownloading: false,
                });
            } finally {
                setSubmitting(false);
            }
        },
        [user, filters, client, rightsholderAttribution, t],
    );
};
